import { useState, useEffect } from 'react';
import { db, storage } from '../firebase';
import logger from '../utils/logger';
import {
  collection,
  addDoc,
  query,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import userService from '../services/userService';
import xpService from '../services/xpService';

/**
 * usePosts - Manages posts state and all post-related operations.
 *
 * Handles creating posts, liking, retweeting, commenting, deleting,
 * pinning, and the real-time posts listener.
 */
export const usePosts = ({ currentUser, setCurrentUser, isLoggedIn, browsingMode, setToastMessage }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPost, setNewPost] = useState('');
  const [postType, setPostType] = useState('regular');
  const [showTradingPost, setShowTradingPost] = useState(false);
  const [feedFilter, setFeedFilter] = useState('all');
  const [postImage, setPostImage] = useState(null);
  const [postImagePreview, setPostImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [postSentiment, setPostSentiment] = useState(null);
  const [hiddenPosts, setHiddenPosts] = useState([]);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [popularTimeFilter, setPopularTimeFilter] = useState('day');

  // Comment state
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [viewingPost, setViewingPost] = useState(null);
  const [postComments, setPostComments] = useState([]);
  const [replyingTo, setReplyingTo] = useState(null);

  // Subscribe to posts
  useEffect(() => {
    if (!isLoggedIn && !browsingMode) return;

    const q = query(collection(db, 'posts'), orderBy('timestamp', 'desc'), limit(25));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const postsData = snapshot.docs.map((docSnapshot) => {
          const data = docSnapshot.data();
          return {
            id: docSnapshot.id,
            ...data,
            gender: data.gender || null,
            username: data.username || 'Unknown',
            handle: data.handle || '@unknown',
            avatar: data.avatar || '',
          };
        });

        setPosts(postsData);
        setLoading(false);

        // Enhance with user data asynchronously
        const enhanceWithUserData = async () => {
          try {
            const enhanced = await Promise.all(
              postsData.map(async (post) => {
                if (post.userId) {
                  try {
                    const userDoc = await getDoc(doc(db, 'users', post.userId));
                    if (userDoc.exists()) {
                      const userData = userDoc.data();
                      return {
                        ...post,
                        gender: userData.gender || post.gender,
                        username: userData.username || post.username,
                        handle: userData.handle || post.handle,
                        avatar: userData.avatar || post.avatar,
                      };
                    }
                  } catch (err) {
                    // Silently handle individual user data fetch errors
                  }
                }
                return post;
              })
            );
            setPosts(enhanced);
          } catch (err) {
            logger.error('Error enhancing posts:', err);
          }
        };

        enhanceWithUserData();
      },
      (err) => {
        logger.error('Error in posts listener:', err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [isLoggedIn, browsingMode]);

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Image must be less than 5MB');
        return;
      }
      setPostImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPostImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePost = async () => {
    if (!newPost.trim() || !currentUser) return;

    try {
      setUploadingImage(true);

      const postCheck = await userService.canUserPost(currentUser.uid);
      if (!postCheck.canPost) {
        alert(`❌ ${postCheck.reason}`);
        setUploadingImage(false);
        return;
      }

      if (postCheck.remaining === 0) {
        alert('❌ You have reached your daily post limit. Rank up to post more!');
        setUploadingImage(false);
        return;
      }

      let imageUrl = null;
      if (postImage) {
        const imageRef = ref(storage, `posts/${currentUser.uid}/${Date.now()}_${postImage.name}`);
        const snapshot = await uploadBytes(imageRef, postImage);
        imageUrl = await getDownloadURL(snapshot.ref);
      }

      if (!currentUser.username || !currentUser.handle || !currentUser.avatar) {
        alert('Error: User profile is incomplete. Please update your profile.');
        return;
      }

      const postData = {
        userId: currentUser.uid,
        username: currentUser.username,
        handle: currentUser.handle,
        avatar: currentUser.avatar,
        content: newPost,
        imageUrl,
        sentiment: postSentiment,
        likes: [],
        retweets: 0,
        replies: 0,
        timestamp: serverTimestamp(),
        type: postType || 'regular',
      };

      const docRef = await addDoc(collection(db, 'posts'), postData);

      await userService.incrementPostCount(currentUser.uid);

      // Create notifications for followers
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const followers = [];
      usersSnapshot.forEach((userDoc) => {
        const userData = userDoc.data();
        if (userData.following && userData.following.includes(currentUser.uid)) {
          followers.push({ uid: userDoc.id, ...userData });
        }
      });

      const notificationPromises = followers.map((follower) =>
        addDoc(collection(db, 'notifications'), {
          userId: follower.uid,
          type: 'post',
          fromUser: currentUser.username,
          fromUserId: currentUser.uid,
          fromAvatar: currentUser.avatar,
          postId: docRef.id,
          postContent: newPost.substring(0, 100),
          read: false,
          timestamp: serverTimestamp(),
        })
      );

      await Promise.all(notificationPromises);

      setNewPost('');
      setPostImage(null);
      setPostImagePreview(null);
      setPostSentiment(null);
    } catch (err) {
      logger.error('Error posting:', err);
      alert('Error creating post: ' + err.message);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleTradingPost = async (tradingPostData) => {
    try {
      const postCheck = await userService.canUserPost(currentUser.uid);
      if (!postCheck.canPost) {
        setToastMessage({ type: 'error', message: `❌ ${postCheck.reason}` });
        return { success: false, error: postCheck.reason };
      }

      if (postCheck.remaining === 0) {
        setToastMessage({ type: 'error', message: '❌ You have reached your daily post limit. Rank up to post more!' });
        return { success: false, error: 'Daily post limit reached' };
      }

      const postData = {
        userId: currentUser.uid,
        username: currentUser.username,
        handle: currentUser.handle,
        avatar: currentUser.avatar,
        content: tradingPostData.content,
        type: 'trading',
        tradingData: tradingPostData.tradingData,
        likes: [],
        retweets: 0,
        replies: 0,
        timestamp: serverTimestamp(),
      };

      const docRef = await addDoc(collection(db, 'posts'), postData);
      await userService.incrementPostCount(currentUser.uid);

      const xpResult = await xpService.submitTradingPost(currentUser.uid, tradingPostData.tradingData);

      setNewPost('');
      setPostType('regular');
      setShowTradingPost(false);

      setToastMessage({ type: 'success', message: `🎯 Trading post submitted! ${xpResult.message}` });
      return { success: true, postId: docRef.id };
    } catch (err) {
      logger.error('Error creating trading post:', err);
      setToastMessage({ type: 'error', message: 'Error creating trading post: ' + err.message });
      return { success: false, error: err.message };
    }
  };

  const likePost = async (postId) => {
    if (!currentUser) return;

    try {
      const postRef = doc(db, 'posts', postId);
      const post = posts.find((p) => p.id === postId);
      if (!post) return;

      const currentLikes = post.likes || [];
      const isLiked = currentLikes.includes(currentUser.uid);

      // Optimistic update
      const updatedPosts = posts.map((p) => {
        if (p.id === postId) {
          return {
            ...p,
            likes: isLiked
              ? currentLikes.filter((uid) => uid !== currentUser.uid)
              : [...currentLikes, currentUser.uid],
          };
        }
        return p;
      });
      setPosts(updatedPosts);

      if (viewingPost && viewingPost.id === postId) {
        setViewingPost({
          ...viewingPost,
          likes: isLiked
            ? currentLikes.filter((uid) => uid !== currentUser.uid)
            : [...currentLikes, currentUser.uid],
        });
      }

      if (isLiked) {
        await updateDoc(postRef, { likes: arrayRemove(currentUser.uid) });
      } else {
        await updateDoc(postRef, { likes: arrayUnion(currentUser.uid) });

        if (post.userId !== currentUser.uid) {
          await addDoc(collection(db, 'notifications'), {
            userId: post.userId,
            type: 'like',
            fromUser: currentUser.username,
            fromUserId: currentUser.uid,
            fromAvatar: currentUser.avatar,
            postId,
            postContent: post.content.substring(0, 100),
            read: false,
            timestamp: serverTimestamp(),
          });
        }
      }
    } catch (err) {
      logger.error('Error liking post:', err);
    }
  };

  const retweetPost = async (postId) => {
    if (!currentUser) return;

    try {
      const post = posts.find((p) => p.id === postId);
      if (!post || post.userId === currentUser.uid) return;

      const existingRetweet = posts.find(
        (p) => p.userId === currentUser.uid && p.retweetOf === postId
      );

      if (existingRetweet) {
        // Optimistic update
        setPosts(
          posts.map((p) =>
            p.id === postId ? { ...p, retweets: Math.max(0, (p.retweets || 0) - 1) } : p
          )
        );

        if (viewingPost && viewingPost.id === postId) {
          setViewingPost({ ...viewingPost, retweets: Math.max(0, (viewingPost.retweets || 0) - 1) });
        }

        await deleteDoc(doc(db, 'posts', existingRetweet.id));
        await updateDoc(doc(db, 'posts', postId), {
          retweets: Math.max(0, (post.retweets || 0) - 1),
        });
      } else {
        // Optimistic update
        setPosts(
          posts.map((p) =>
            p.id === postId ? { ...p, retweets: (p.retweets || 0) + 1 } : p
          )
        );

        if (viewingPost && viewingPost.id === postId) {
          setViewingPost({ ...viewingPost, retweets: (viewingPost.retweets || 0) + 1 });
        }

        await addDoc(collection(db, 'posts'), {
          userId: currentUser.uid,
          username: currentUser.username,
          handle: currentUser.handle,
          avatar: currentUser.avatar,
          content: post.content,
          retweetOf: postId,
          originalAuthor: post.username,
          originalHandle: post.handle,
          originalAvatar: post.avatar,
          originalUserId: post.userId,
          originalGender: post.gender,
          likes: [],
          retweets: 0,
          replies: 0,
          timestamp: serverTimestamp(),
        });

        await updateDoc(doc(db, 'posts', postId), {
          retweets: (post.retweets || 0) + 1,
        });

        if (post.userId !== currentUser.uid) {
          await addDoc(collection(db, 'notifications'), {
            userId: post.userId,
            type: 'retweet',
            fromUser: currentUser.username,
            fromUserId: currentUser.uid,
            fromAvatar: currentUser.avatar,
            postId,
            postContent: post.content.substring(0, 100),
            read: false,
            timestamp: serverTimestamp(),
          });
        }
      }
    } catch (err) {
      logger.error('Error retweeting:', err);
    }
  };

  const handleComment = async () => {
    if (!newComment.trim() || !currentUser) return;

    try {
      const targetPost = viewingPost || selectedPost;
      if (!targetPost) return;

      await addDoc(collection(db, 'comments'), {
        postId: targetPost.id,
        userId: currentUser.uid,
        username: currentUser.username,
        handle: currentUser.handle,
        avatar: currentUser.avatar,
        content: newComment,
        likes: [],
        timestamp: serverTimestamp(),
      });

      await updateDoc(doc(db, 'posts', targetPost.id), {
        replies: (targetPost.replies || 0) + 1,
      });

      if (targetPost.userId !== currentUser.uid) {
        await addDoc(collection(db, 'notifications'), {
          userId: targetPost.userId,
          type: 'comment',
          fromUser: currentUser.username,
          fromUserId: currentUser.uid,
          fromAvatar: currentUser.avatar,
          postId: targetPost.id,
          postContent: targetPost.content.substring(0, 100),
          commentContent: newComment.substring(0, 100),
          read: false,
          timestamp: serverTimestamp(),
        });
      }

      setNewComment('');
      setShowCommentModal(false);
      setSelectedPost(null);
      setReplyingTo(null);
    } catch (err) {
      logger.error('Error commenting:', err);
    }
  };

  const likeComment = async (commentId) => {
    if (!currentUser) return;

    try {
      const commentRef = doc(db, 'comments', commentId);
      const comment = postComments.find((c) => c.id === commentId);
      if (!comment) return;

      const currentLikes = comment.likes || [];
      const isLiked = currentLikes.includes(currentUser.uid);

      setPostComments(
        postComments.map((c) => {
          if (c.id === commentId) {
            return {
              ...c,
              likes: isLiked
                ? currentLikes.filter((uid) => uid !== currentUser.uid)
                : [...currentLikes, currentUser.uid],
            };
          }
          return c;
        })
      );

      if (isLiked) {
        await updateDoc(commentRef, { likes: arrayRemove(currentUser.uid) });
      } else {
        await updateDoc(commentRef, { likes: arrayUnion(currentUser.uid) });
      }
    } catch (err) {
      logger.error('Error liking comment:', err);
    }
  };

  const handleDeleteComment = async (commentId, postId) => {
    try {
      await deleteDoc(doc(db, 'comments', commentId));

      const post = posts.find((p) => p.id === postId);
      if (post) {
        await updateDoc(doc(db, 'posts', postId), {
          replies: Math.max(0, (post.replies || 0) - 1),
        });
      }
    } catch (err) {
      logger.error('Error deleting comment:', err);
    }
  };

  const handleDeletePost = async (postId) => {
    try {
      const postDoc = await getDoc(doc(db, 'posts', postId));

      if (postDoc.exists()) {
        const postData = postDoc.data();
        if (postData.type === 'prediction' && postData.predictionData) {
          const predictionData = postData.predictionData;
          if (predictionData.status === 'active' || !predictionData.isResolved) {
            alert("❌ Active predictions cannot be deleted until completed! No givebacks or oopsies - when you confirm, it's confirmed until the end.");
            return;
          }
        }
      }

      await deleteDoc(doc(db, 'posts', postId));
      setPosts(posts.filter((p) => p.id !== postId));
    } catch (err) {
      logger.error('Error deleting post:', err);
      alert('Error deleting post: ' + err.message);
    }
  };

  const handlePinPost = async (postId) => {
    if (!currentUser) return;

    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const currentPinnedPost = currentUser.pinnedPost;

      if (currentPinnedPost === postId) {
        await updateDoc(userRef, { pinnedPost: '' });
        setCurrentUser({ ...currentUser, pinnedPost: '' });
        setToastMessage('Post unpinned from profile');
      } else {
        await updateDoc(userRef, { pinnedPost: postId });
        setCurrentUser({ ...currentUser, pinnedPost: postId });
        setToastMessage('Post pinned to profile');
      }
    } catch (err) {
      logger.error('Error pinning post:', err);
      alert('Error pinning post: ' + err.message);
    }
  };

  const getPopularPosts = () => {
    const now = new Date();
    const dayAgo = new Date(now - 24 * 60 * 60 * 1000);
    const weekAgo = new Date(now - 7 * 24 * 60 * 60 * 1000);

    let filteredPosts = [...posts];

    if (popularTimeFilter === 'day') {
      filteredPosts = posts.filter((p) => {
        const postDate = p.timestamp?.toDate ? p.timestamp.toDate() : new Date(p.timestamp);
        return postDate > dayAgo;
      });
    } else if (popularTimeFilter === 'week') {
      filteredPosts = posts.filter((p) => {
        const postDate = p.timestamp?.toDate ? p.timestamp.toDate() : new Date(p.timestamp);
        return postDate > weekAgo;
      });
    }

    return filteredPosts.sort((a, b) => {
      const aScore = (a.likes?.length || 0) + (a.retweets || 0) + (a.replies || 0);
      const bScore = (b.likes?.length || 0) + (b.retweets || 0) + (b.replies || 0);
      return bScore - aScore;
    });
  };

  return {
    // State
    posts,
    setPosts,
    loading,
    newPost,
    setNewPost,
    postType,
    setPostType,
    showTradingPost,
    setShowTradingPost,
    feedFilter,
    setFeedFilter,
    postImage,
    setPostImage,
    postImagePreview,
    setPostImagePreview,
    uploadingImage,
    postSentiment,
    setPostSentiment,
    hiddenPosts,
    setHiddenPosts,
    activeDropdown,
    setActiveDropdown,
    popularTimeFilter,
    setPopularTimeFilter,
    showCommentModal,
    setShowCommentModal,
    selectedPost,
    setSelectedPost,
    newComment,
    setNewComment,
    viewingPost,
    setViewingPost,
    postComments,
    setPostComments,
    replyingTo,
    setReplyingTo,

    // Handlers
    handleImageSelect,
    handlePost,
    handleTradingPost,
    likePost,
    retweetPost,
    handleComment,
    likeComment,
    handleDeleteComment,
    handleDeletePost,
    handlePinPost,
    getPopularPosts,
  };
};
